import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import useApi from "../../hook/useApi";
import Loader from "../Loader/Loader";
import { useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import JoditEditor from "jodit-react";

const INITIAL_FORM = {
  title: "",
  slug: "",
  description: "",
};

const generateSlugFromTitle = (title = "") => {
  return title
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
};

const getPlainTextFromHtml = (html = "") => {
  if (!html) return "";
  const tempElement = document.createElement("div");
  tempElement.innerHTML = html;
  return tempElement.textContent || tempElement.innerText || "";
};

const CMSManagementLayer = () => {
  const { AsyncGetApiCall, AsyncPostApiCall, AsyncDeleteApiCall } = useApi();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDev = searchParams.get("dev") === "true";
  const editor = useRef(null);

  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [showFormModal, setShowFormModal] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [isSlugEdited, setIsSlugEdited] = useState(false);

  const editorConfig = useMemo(
    () => ({
      readonly: false,
      placeholder: "Enter CMS Description",
      minHeight: 260,
    }),
    [],
  );

  const fetchPages = async () => {
    setLoading(true);
    const res = await AsyncGetApiCall("/pages/list");
    if (res?.errors) {
      toast.error(res.errors[0] || "Failed to fetch pages");
    } else if (res?.success) {
      setList(res.data || []);
    } else {
      toast.error(res?.message || "Failed to fetch pages");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPages();
  }, []);

  const openAddModal = () => {
    setFormData(INITIAL_FORM);
    setIsSlugEdited(false);
    setShowFormModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      title: formData.title.trim(),
      slug: formData.slug.trim(),
      description: formData.description.trim(),
    };

    if (!payload.title) return toast.error("Title is required");
    if (!payload.slug) return toast.error("Slug is required");
    if (!payload.description) return toast.error("Description is required");

    setLoading(true);
    const res = await AsyncPostApiCall("/pages/add", payload);

    if (res?.errors) {
      toast.error(res.errors[0] || "Request failed");
    } else if (res?.success) {
      toast.success(res.message || "Added successfully");
      setShowFormModal(false);
      setFormData(INITIAL_FORM);
      setIsSlugEdited(false);
      fetchPages();
    } else {
      toast.error(res?.message || "Request failed");
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    const { isConfirmed } = await Swal.fire({
      title: "Delete CMS?",
      text: "Are you sure you want to delete this CMS?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2352c3",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Delete it!",
      customClass: { popup: "swal-sm" },
    });

    if (!isConfirmed) {
      return;
    }

    setLoading(true);
    const res = await AsyncDeleteApiCall(`/pages/delete/${id}`);
    if (res?.errors) {
      toast.error(res.errors[0] || "Delete failed");
    } else if (res?.success) {
      toast.success(res.message || "Deleted successfully");
      fetchPages();
    } else {
      toast.error(res?.message || "Delete failed");
    }
    setLoading(false);
  };

  if (loading && list.length === 0) return <Loader />;

  return (
    <>
      {loading && <Loader />}
      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex justify-content-between align-items-center">
          <h6 className="text-lg fw-semibold mb-0">CMS Management</h6>
          {isDev && (
            <button
              type="button"
              onClick={openAddModal}
              className="btn btn-primary-600 btn-sm d-flex align-items-center gap-2"
            >
              <Icon icon="lucide:plus" /> Add CMS
            </button>
          )}
        </div>

        <div className="card-body p-24">
          <div className="table-responsive">
            <table className="table bordered-table mb-0">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Title</th>
                  <th>Slug</th>
                  <th>Description</th>
                  <th>Updated At</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {list.length > 0 ? (
                  list.map((item, index) => (
                    // Show plain text preview in list even when saved as rich HTML.
                    <tr key={item._id}>
                      <td>{index + 1}</td>
                      <td>
                        {item.title.length > 10
                          ? item.title.slice(0, 10) + "..."
                          : item.title || "-"}
                      </td>
                      <td>{item.slug || "-"}</td>
                      <td>
                        {(() => {
                          const plainDescription = getPlainTextFromHtml(
                            item.description,
                          ).trim();
                          if (!plainDescription) return "-";
                          return plainDescription.length > 50
                            ? `${plainDescription.slice(0, 50)}...`
                            : plainDescription;
                        })()}
                      </td>
                      <td>{new Date(item.updatedAt).toLocaleString()}</td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <button
                            type="button"
                            className="bg-info-focus text-info-600 bg-hover-info-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                            onClick={() =>
                              navigate(`/cms-management/view/${item._id}`)
                            }
                            title="View"
                          >
                            <Icon
                              icon="majesticons:eye-line"
                              className="icon"
                            />
                          </button>
                          <button
                            type="button"
                            className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                            onClick={() =>
                              navigate(`/cms-management/update/${item._id}`)
                            }
                            title="Edit"
                          >
                            <Icon icon="lucide:edit" className="icon" />
                          </button>
                          <button
                            type="button"
                            className="bg-danger-focus text-danger-600 bg-hover-danger-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                            onClick={() => handleDelete(item._id)}
                            title="Delete"
                          >
                            <Icon icon="lucide:trash-2" className="icon" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="6"
                      className="text-center py-24 text-secondary-light"
                    >
                      No pages found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showFormModal && (
        <div
          className="modal fade show d-block modal-lg"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add CMS</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowFormModal(false)}
                />
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Title *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData((prev) => {
                          const nextTitle = e.target.value;
                          return {
                            ...prev,
                            title: nextTitle,
                            slug: isSlugEdited
                              ? prev.slug
                              : generateSlugFromTitle(nextTitle),
                          };
                        })
                      }
                      placeholder="Enter CMS Title"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Slug *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.slug}
                      onChange={(e) => {
                        setIsSlugEdited(true);
                        setFormData((prev) => ({
                          ...prev,
                          slug: e.target.value,
                        }));
                      }}
                      placeholder="Enter CMS Slug"
                    />
                    <small className="text-secondary-light">
                      Slug is auto-generated from title, but you can edit it.
                    </small>
                  </div>
                  <div>
                    <label className="form-label">Description *</label>
                    <JoditEditor
                      ref={editor}
                      value={formData.description}
                      config={editorConfig}
                      onBlur={(newContent) =>
                        setFormData((prev) => ({
                          ...prev,
                          description: newContent,
                        }))
                      }
                      onChange={() => {}}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowFormModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary-600">
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CMSManagementLayer;
